import { renewableToken, replaceRenewedToken, tokenExpMs } from "./session.ts";

/**
 * 续票（POST /admin/api/session/renew，规则见 src/admin/session-renew.ts）。
 *
 * 只在**人在用**的时候续：最近 ACTIVE_WITHIN_MS 内有过点击 / 按键 / 滚轮。不能拿「发过请求」当在用——
 * 健康探测、绑定状态轮询、协同编辑这些自己会发请求，按请求算的话开着一个标签页没人看也会一直续下去。
 * 票剩不到 RENEW_WHEN_LEFT_MS 才续，所以一直在用时大约两小时续一次；放着不动 8 小时后照常过期。
 * 续失败（账号停用、满 7 天、网络）不打断：旧票照用到期，到期后的 401 再回登录页。
 */
const RENEW_WHEN_LEFT_MS = 6 * 60 * 60_000;
const ACTIVE_WITHIN_MS = 30 * 60_000;
const KEEPER_TICK_MS = 60_000;
let lastActivityMs = Date.now();
let renewing: Promise<void> | null = null;
/** 服务端拒过续的那张票（停用 / 满 7 天）：同一张票不再每分钟去问一遍，换了票（重新登录）才重新开始 */
let refused: string | null = null;

/** 测试用：把「最近一次操作」拨到指定时刻 */
export const markActivityAt = (ms: number) => { lastActivityMs = ms; };

export function renewIfDue(now = Date.now()): Promise<void> | null {
  if (renewing) return renewing;
  const t = renewableToken();
  if (!t || t === refused || now - lastActivityMs > ACTIVE_WITHIN_MS) return null;
  const left = tokenExpMs(t) - now;
  if (left <= 0 || left > RENEW_WHEN_LEFT_MS) return null;
  renewing = fetch("/admin/api/session/renew", { method: "POST", headers: { Authorization: `Bearer ${t}` } })
    .then(async (r) => {
      const body = (await r.json()) as { code?: string; data?: { token?: string } };
      if (body.code === "0" && body.data?.token) replaceRenewedToken(t, body.data.token);
      // 403 是服务端明确不给续；5xx、网络错误下一分钟再试
      else if (r.status === 403) refused = t;
    })
    .catch(() => { /* 见上：续不上就等旧票到期 */ })
    .finally(() => { renewing = null; });
  return renewing;
}

/** App 挂载时调一次：记人的操作、每分钟看一眼要不要续。返回卸载函数 */
export function startSessionKeeper(): () => void {
  const mark = () => { lastActivityMs = Date.now(); };
  const events = ["pointerdown", "keydown", "wheel"] as const;
  for (const e of events) window.addEventListener(e, mark, { passive: true, capture: true });
  // 切回标签页时先看一眼：电脑睡了一晚，计时器没跑，回来第一件事是续上（还没过期的话）
  const onVisible = () => { if (document.visibilityState === "visible") void renewIfDue(); };
  document.addEventListener("visibilitychange", onVisible);
  const timer = window.setInterval(() => void renewIfDue(), KEEPER_TICK_MS);
  return () => {
    for (const e of events) window.removeEventListener(e, mark, { capture: true });
    document.removeEventListener("visibilitychange", onVisible);
    window.clearInterval(timer);
  };
}
